/**
 * OpsScopeBar — App × 语言两行芯片 + 无覆盖/已覆盖。
 *
 * 1:1 移植 design/Novel-admin/src/pages/ops-scope.jsx 的 ScopeBar。
 */

import { type ReactNode, useMemo } from 'react';
import { useApps, useSysConfig } from '~/data/queries';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import {
  type OpsModule,
  type OpsScopeKind,
  SCOPE_META,
  deriveOpsScope,
  isOpsGlobal,
  opsCopyButtonDisabled,
  opsCopyButtonTitle,
  opsDirectCreateButtonDisabled,
  opsDirectCreateButtonTitle,
  usesAppDedicatedCopy,
} from './ops-scope';

export type OpsConfigSource = 'GLOBAL' | 'GLOBAL_INHERITED' | 'APP_DEDICATED';

/** 旧三值 → 两轴 scope（接真列表仍可读） */
export function deriveOpsConfigSource(appId: string): OpsConfigSource {
  return isOpsGlobal(appId) ? 'GLOBAL' : 'GLOBAL_INHERITED';
}

export function OpsScopeBar({
  module,
  appId,
  lang,
  onApp,
  onLang,
  serverHasOverride,
  globalN,
  overrideN,
  onCopy,
  onDirectCreate,
  onRemove,
  languageAllowed = true,
  note,
}: {
  module: OpsModule;
  appId: string;
  lang: string;
  onApp: (id: string) => void;
  onLang: (code: string) => void;
  serverHasOverride?: boolean;
  globalN: number;
  overrideN: number;
  onCopy: () => void;
  onDirectCreate: () => void;
  onRemove: () => void;
  languageAllowed?: boolean;
  note?: ReactNode;
}) {
  const { data: apps } = useApps();
  const { data: sys } = useSysConfig();
  const scope: OpsScopeKind = deriveOpsScope(module, appId, lang, Boolean(serverHasOverride));
  const meta = SCOPE_META[scope];

  const langs = useMemo(() => {
    const codes = sys.languages
      .filter((l) => l.enabled && l.language_code !== 'zh')
      .map((l) => l.language_code);
    return ['en', ...codes.filter((c) => c !== 'en')];
  }, [sys.languages]);

  const exampleApps = apps;

  return (
    <div className="card" style={{ padding: '10px 14px 12px' }}>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="label" style={{ width: 30 }}>
          App
        </span>
        <button
          type="button"
          className={`btn btn-sm ${isOpsGlobal(appId) ? 'btn-accent' : ''}`}
          title="该语言的全局配置（app_id 空）"
          onClick={() => onApp('all')}
        >
          全部
        </button>
        {exampleApps.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`btn btn-sm ${a.id === appId ? 'btn-accent' : ''}`}
            title="该 App 的可选覆盖"
            onClick={() => onApp(a.id)}
          >
            {a.name}
            {a.id === appId && serverHasOverride && (
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  background: a.id === appId ? 'white' : 'var(--pos)',
                  marginLeft: 5,
                }}
                title="该语言已有覆盖"
              />
            )}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {!isOpsGlobal(appId) && (
          <>
            <span className={`chip ${meta.cls}`} style={{ fontSize: 10 }}>
              {meta.label}
            </span>
            {usesAppDedicatedCopy(module) &&
              (scope === 'NO_OVERRIDE' ? (
                <button
                  type="button"
                  className="btn btn-sm btn-accent"
                  disabled={opsCopyButtonDisabled(globalN, languageAllowed)}
                  title={
                    opsCopyButtonTitle(globalN, languageAllowed, lang) ??
                    `复制该语言全局的 ${globalN} 条`
                  }
                  onClick={onCopy}
                >
                  <Icons.IconCopy size={11} />
                  从全局复制
                </button>
              ) : null)}
            {usesAppDedicatedCopy(module) &&
              (scope === 'NO_OVERRIDE' ? (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={opsDirectCreateButtonDisabled(languageAllowed)}
                  title={
                    opsDirectCreateButtonTitle(languageAllowed, lang) ??
                    '只建身份行、不搬内容，落地即「已覆盖 + 0 条」'
                  }
                  onClick={onDirectCreate}
                >
                  <Icons.IconPlus size={11} />
                  直接创建空覆盖
                </button>
              ) : (
                <button type="button" className="btn btn-sm btn-danger" onClick={onRemove}>
                  <Icons.IconTrash size={11} />
                  移除覆盖
                </button>
              ))}
          </>
        )}
      </div>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <span className="label" style={{ width: 30 }}>
          语言
        </span>
        {langs.map((code) => (
          <button
            key={code}
            type="button"
            className={`btn btn-sm ${code === lang ? 'btn-accent' : ''}`}
            onClick={() => onLang(code)}
          >
            <span className="mono">{code.toUpperCase()}</span>
            <span style={{ marginLeft: 3, opacity: 0.8 }}>{langName(code)}</span>
          </button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 9, lineHeight: 1.6 }}>
        {scope === 'GLOBAL' ? (
          <>
            「全部」= <span className="mono">app_id</span> 空 +{' '}
            <span className="mono">language={lang}</span> 的
            <span style={{ fontWeight: 500 }}>该语言全局</span>
            配置：不覆盖该语言的 App 全部读这一份，不是各 App 聚合。
          </>
        ) : scope === 'NO_OVERRIDE' ? (
          <>
            该 App 在 <span className="mono">{lang}</span> 下
            <span style={{ fontWeight: 500 }}>没有覆盖</span>
            ：C 端读该语言全局。本表不列全局行 —— 需要差异化先「从全局复制」，
            全局层为空或内容只属本 App 时走「直接创建空覆盖」。
          </>
        ) : (
          <>
            该 App 在 <span className="mono">{lang}</span> 下
            <span style={{ fontWeight: 500 }}>已有覆盖</span>
            ：表里只画覆盖的 {overrideN} 条，C 端只读覆盖，不与全局并集、不 fallback 英语。
          </>
        )}
        {note && <div style={{ marginTop: 4 }}>{note}</div>}
      </div>
    </div>
  );
}
